import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

interface RollInRequestFormProps {
  workspaceId: string;
  projectId?: string;
  onSuccess?: () => void;
}

export function RollInRequestForm({ workspaceId, projectId, onSuccess }: RollInRequestFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    new_account_number: '',
    existing_account_id: '',
    target_customer_id: '',
    target_site_id: '',
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    requested_effective_date: '',
    mapping_rules: {
      auto_map_bills: true,
      preserve_history: true,
      merge_duplicates: false,
    },
  });

  // Fetch customers for selection
  const { data: customers } = useQuery({
    queryKey: ['customers', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name')
        .eq('workspace_id', workspaceId)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId,
  });

  // Fetch sites for selected customer
  const { data: sites } = useQuery({
    queryKey: ['sites', workspaceId, formData.target_customer_id],
    queryFn: async () => {
      if (!formData.target_customer_id) return [];
      
      const { data, error } = await supabase
        .from('sites')
        .select('id, name')
        .eq('workspace_id', workspaceId)
        .eq('customer_id', formData.target_customer_id)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId && !!formData.target_customer_id,
  });

  // Fetch existing accounts for selection
  const { data: existingAccounts } = useQuery({
    queryKey: ['accounts', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, account_number, sites!inner(name, customers!inner(name))')
        .eq('workspace_id', workspaceId)
        .order('account_number');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId,
  });

  const createRequestMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const requestData = {
        new_account_number: formData.new_account_number,
        existing_account_id: formData.existing_account_id || null,
        target_customer_id: formData.target_customer_id,
        target_site_id: formData.target_site_id,
        mapping_rules: formData.mapping_rules,
      };

      const { data, error } = await supabase
        .from('tum_change_management_requests')
        .insert({
          workspace_id: workspaceId,
          onboarding_project_id: projectId || null,
          request_type: 'roll_in',
          request_data: requestData,
          description: formData.description,
          priority: formData.priority,
          requested_effective_date: formData.requested_effective_date || null,
          requested_by: user.id,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-management-counts', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['change-management-requests', workspaceId] });
      toast({
        title: 'Roll In request submitted',
        description: 'Your request has been submitted and is pending approval.',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error submitting request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.new_account_number.trim()) {
      toast({
        title: 'Account number required',
        description: 'Please enter the new account number',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.target_customer_id) {
      toast({
        title: 'Target customer required',
        description: 'Please select a target customer',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.target_site_id) {
      toast({
        title: 'Target site required',
        description: 'Please select a target site',
        variant: 'destructive',
      });
      return;
    }

    createRequestMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRight className="h-5 w-5" />
          Roll In Request
        </CardTitle>
        <CardDescription>
          Add a new account to an existing account mapping. This will merge the new account into your existing setup.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Account Number */}
          <div className="space-y-2">
            <Label htmlFor="newAccountNumber">New Account Number *</Label>
            <Input
              id="newAccountNumber"
              value={formData.new_account_number}
              onChange={(e) => setFormData(prev => ({ ...prev, new_account_number: e.target.value }))}
              placeholder="Enter new account number"
              required
            />
          </div>

          {/* Existing Account (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="existingAccount">Existing Account to Map To (Optional)</Label>
            <Select
              value={formData.existing_account_id || 'none'}
              onValueChange={(value) => setFormData(prev => ({ ...prev, existing_account_id: value === 'none' ? '' : value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select existing account (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None - Create new mapping</SelectItem>
                {existingAccounts?.map((account: any) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.account_number} - {account.sites?.name || 'Unknown Site'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              If selected, the new account will be mapped to this existing account
            </p>
          </div>

          {/* Target Customer */}
          <div className="space-y-2">
            <Label htmlFor="targetCustomer">Target Customer *</Label>
            <Select
              value={formData.target_customer_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, target_customer_id: value, target_site_id: '' }))}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select target customer" />
              </SelectTrigger>
              <SelectContent>
                {customers?.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target Site */}
          <div className="space-y-2">
            <Label htmlFor="targetSite">Target Site *</Label>
            <Select
              value={formData.target_site_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, target_site_id: value }))}
              disabled={!formData.target_customer_id}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder={formData.target_customer_id ? "Select target site" : "Select customer first"} />
              </SelectTrigger>
              <SelectContent>
                {sites?.map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mapping Rules */}
          <div className="space-y-4 p-4 border rounded-lg">
            <Label className="text-base font-semibold">Mapping Rules</Label>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="autoMapBills">Auto-map Bills</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically map bills from the new account to the target account
                </p>
              </div>
              <input
                type="checkbox"
                id="autoMapBills"
                checked={formData.mapping_rules.auto_map_bills}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  mapping_rules: { ...prev.mapping_rules, auto_map_bills: e.target.checked }
                }))}
                className="h-4 w-4"
                aria-label="Auto-map bills"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="preserveHistory">Preserve History</Label>
                <p className="text-xs text-muted-foreground">
                  Keep historical data from the new account
                </p>
              </div>
              <input
                type="checkbox"
                id="preserveHistory"
                checked={formData.mapping_rules.preserve_history}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  mapping_rules: { ...prev.mapping_rules, preserve_history: e.target.checked }
                }))}
                className="h-4 w-4"
                aria-label="Preserve history"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="mergeDuplicates">Merge Duplicates</Label>
                <p className="text-xs text-muted-foreground">
                  Merge duplicate records if found
                </p>
              </div>
              <input
                type="checkbox"
                id="mergeDuplicates"
                checked={formData.mapping_rules.merge_duplicates}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  mapping_rules: { ...prev.mapping_rules, merge_duplicates: e.target.checked }
                }))}
                className="h-4 w-4"
                aria-label="Merge duplicates"
              />
            </div>
          </div>

          {/* Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="effectiveDate">Effective Date (Optional)</Label>
              <Input
                id="effectiveDate"
                type="date"
                value={formData.requested_effective_date}
                onChange={(e) => setFormData(prev => ({ ...prev, requested_effective_date: e.target.value }))}
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description / Notes</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Additional information about this roll-in request..."
              rows={4}
            />
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This request will be reviewed by workspace administrators. You'll be notified once it's approved or if additional information is needed.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button 
              type="submit"
              disabled={createRequestMutation.isPending}
              className="flex-1"
            >
              {createRequestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

